import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useChartColors } from "./chartColors";
import { Panel, LoadingRows, ErrorState, EmptyState } from "./Panel";
import type { AnalyticsEnvelope, CompositionRow, AnalyticsViewProps } from "./types";

const STATUSES = ["focus", "meeting", "break", "available", "away"] as const;

type CompositionData = AnalyticsEnvelope & {
  series: CompositionRow[];
  totals: Record<string, number>;
};

export function StatusCompositionView({
  data,
  loading,
  error,
}: AnalyticsViewProps<CompositionData>) {
  const { t } = useTranslation();
  const colors = useChartColors();
  const [asTable, setAsTable] = useState(false);
  const rows = (data?.series ?? []).map((r) => ({
    ...r,
    focus: Math.round(r.focus / 3600),
    meeting: Math.round(r.meeting / 3600),
    break: Math.round(r.break / 3600),
    available: Math.round(r.available / 3600),
    away: Math.round(r.away / 3600),
  }));
  const toggle = (
    <button
      type="button"
      className="text-[12px] text-muted-foreground underline"
      onClick={() => setAsTable((v) => !v)}
    >
      {t("homework.analytics.viewAsTable")}
    </button>
  );

  return (
    <Panel
      title={t("homework.analytics.composition.title")}
      action={rows.length ? toggle : undefined}
    >
      {loading ? (
        <LoadingRows />
      ) : error ? (
        <ErrorState message={t("homework.analytics.error")} />
      ) : !rows.length ? (
        <EmptyState message={t("homework.analytics.empty")} />
      ) : asTable ? (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-muted-foreground">
              <th className="text-left">{t("homework.analytics.attendance.date")}</th>
              {STATUSES.map((s) => (
                <th key={s} className="text-right">
                  {t(`homework.status.${s}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.period}>
                <td>{r.period}</td>
                {STATUSES.map((s) => (
                  <td key={s} className="text-right tabular-nums">
                    {r[s]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div role="img" aria-label={t("homework.analytics.composition.title")}>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="period" stroke={colors.axis} tick={{ fontSize: 12, fill: colors.axis }} />
            <YAxis stroke={colors.axis} tick={{ fontSize: 12, fill: colors.axis }} width={36} />
            <Tooltip />
            <Legend />
            {STATUSES.map((s) => (
              <Bar
                key={s}
                dataKey={s}
                stackId="t"
                name={t(`homework.status.${s}`)}
                fill={colors.status[s] ?? colors.series(STATUSES.indexOf(s))}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
        </div>
      )}
      {data?.caveat && <p className="mt-2 text-[11px] text-muted-foreground">{data.caveat}</p>}
    </Panel>
  );
}
